import { router, useLocalSearchParams } from 'expo-router';
import { useState } from 'react';

import { useUpdateNonProfitOrg } from '@/api/hooks/nonProfitOrgHooks';
import NpoPLForm from '@/components/forms/NpoPLForm';
import { useAuth } from '@/contexts/authContext';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { PLData } from '@/lib/validations/NpoOnboardingSchema';
import { containsName, containsSection } from '@/utils/containsSection';
import { safeJsonParseArray } from '@/utils/firebaseImage';
import { i18n } from '@/utils/i18n';
import { uploadToFireStorage } from '@/utils/uploadToFireStorage';

const NpoPL = () => {
  const { pl, balanceSheet } = useLocalSearchParams<{ pl: string; balanceSheet: string }>();
  const { state: authState } = useAuth();
  const { mutateAsync: nonProfitUpdate, isPending: nonProfitIsPending } = useUpdateNonProfitOrg();
  const { showError, showSuccess } = useEndFlowModal();

  const [isLoading, setIsLoading] = useState(false);
  const [fileProgress, setFileProgress] = useState<number | undefined>(undefined);
  const [indexUploading, setIndexUploading] = useState<number | undefined>(undefined);

  const initialData = {
    pL: typeof pl === 'string' ? safeJsonParseArray(pl)[0] : '',
    balanceSheet: typeof balanceSheet === 'string' ? safeJsonParseArray(balanceSheet)[0] : ''
  };

  const npoId = authState.signUpData?.loginEmail?.npoId;

  const onSubmit = async (data: PLData) => {
    try {
      setIsLoading(true);

      let uploadedFiles: {
        pl: string[] | [];
        balanceSheet: string[] | [];
      } = { pl: [], balanceSheet: [] };

      if (data.pL && !containsSection(data.pL, 'pl')) {
        await uploadToFireStorage(
          `non-profits-orgs/${npoId}/pl/`,
          {
            uri: data.pL,
            name: `profit_loss.${new Date().toISOString()}`
          },
          (progress) => {
            setIndexUploading(0);
            setFileProgress(progress);
          }
        ).then((storagedFiles) => {
          uploadedFiles.pl = containsName(storagedFiles.metadata.name, 'profit_loss')
            ? [storagedFiles.metadata.fullPath]
            : [];
        });
      }

      if (data.balanceSheet && !containsSection(data.balanceSheet, 'bs')) {
        await uploadToFireStorage(
          `non-profits-orgs/${npoId}/bs/`,
          {
            uri: data.balanceSheet,
            name: `balance_sheet.${new Date().toISOString()}`
          },
          (progress) => {
            setIndexUploading(1);
            setFileProgress(progress);
          }
        ).then((storagedFiles) => {
          uploadedFiles.balanceSheet = containsName(storagedFiles.metadata.name, 'balance_sheet')
            ? [storagedFiles.metadata.fullPath]
            : storagedFiles && containsName(storagedFiles.metadata.name, 'balance_sheet')
              ? [storagedFiles.metadata.fullPath]
              : [];
        });
      }

      nonProfitUpdate(
        {
          npoId: npoId ?? '',
          plStatement: data.pL ? (uploadedFiles.pl.length === 0 ? null : uploadedFiles.pl) : [],
          balanceSheet: data.balanceSheet
            ? uploadedFiles.balanceSheet.length === 0
              ? null
              : uploadedFiles.balanceSheet
            : []
        },
        {
          onSuccess: () => {
            showSuccess({
              title: i18n.t('end_flow.success_title'),
              message: i18n.t('npo_details.update_message')
            });
            router.back();
          },
          onError: (e) => {
            showError({ message: e.message });
            setIsLoading(false);
            setFileProgress(undefined);
            console.error('Error', e);
          }
        }
      );
    } catch (e) {
      setIsLoading(false);
      setFileProgress(undefined);
      setIndexUploading(undefined);
      console.error(e);
    }
  };

  return (
    <NpoPLForm
      initialData={initialData}
      isLoading={nonProfitIsPending || isLoading}
      showBackButton={false}
      showSaveAndContinueButton={false}
      submitLabel={i18n.t('mix.done')}
      indexUploading={indexUploading}
      fileProgress={fileProgress}
      onSubmitTapped={onSubmit}
    />
  );
};

export default NpoPL;
